/* eslint-disable react-refresh/only-export-components */
import React, { useState, lazy, Suspense } from 'react'
import { lazyWithRetry } from './lib/lazyWithRetry.js'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ReactDOM from 'react-dom/client'
import PazqueApp from './App.jsx';
import { AppProvider } from './context/AppContext.tsx';
import { SB_URL, SKEY as SB_KEY, getOrgId} from './lib/constants.js';
import { useErrorReporting } from './hooks/useErrorReporting.ts';
import { setSentryUser, Sentry } from './lib/sentry.js';
// sentry.js se auto-inicializa al importarse
const OnboardingWizard = lazyWithRetry(() => import('./tabs/OnboardingWizard.jsx'));
const CatalogoPage     = lazyWithRetry(() => import('./pages/CatalogoPage.jsx'));
const PedidosPage      = lazyWithRetry(() => import('./pages/PedidosPage.jsx'));
const VendedorPage     = lazyWithRetry(() => import('./pages/VendedorPage.jsx'));
const RegisterPage     = lazyWithRetry(() => import('./pages/RegisterPage.jsx'));
const LandingPage      = lazyWithRetry(() => import('./pages/LandingPage.jsx'));
const UpgradePage      = lazyWithRetry(() => import('./pages/UpgradePage.jsx'));
const DriverView       = lazyWithRetry(() => import('./pages/DriverView.jsx'));
const TrackingPage     = lazyWithRetry(() => import('./pages/TrackingPage.jsx'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage.jsx'));
const TermsPage   = lazyWithRetry(() => import('./pages/TermsPage.jsx'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage.jsx'));
const OwnerPage   = lazyWithRetry(() => import('./pages/OwnerPage.jsx'));
const ONBOARDING_KEY = 'stock-onboarding-done';

// ── Demo mode ─────────────────────────────────────────────────────
import DemoSelector from './demo/DemoSelector.jsx';
import DemoBanner from './demo/DemoBanner.jsx';
import DemoToast from './demo/DemoToast.jsx';
import { useDemo } from './demo/useDemo.js';

function readSession() {
  try {
    const s = JSON.parse(localStorage.getItem('aryes-session') || 'null');
    if (s && s.expiresAt != null && Date.now() > s.expiresAt) {
      localStorage.removeItem('aryes-session');
      return null;
    }
    return s;
  } catch { return null; }
}

// Recuperación dura para cuando la app quedó rota por un chunk viejo tras un
// deploy. El error NO vive en localStorage (lo que limpiaba el botón antes era
// inútil): vive en el Service Worker y en el Cache Storage del navegador, que
// siguen sirviendo un index.html muerto. Des-registramos el SW, borramos todas
// las cachés y recargamos con cache-bust para saltar la caché HTTP del navegador.
async function hardReset() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    }
    if (window.caches?.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
    }
  } catch {}
  try { localStorage.removeItem('pazque_chunk_reload'); sessionStorage.removeItem('pazque_chunk_reload'); } catch {}
  // Cache-bust: un query nuevo fuerza al navegador a traer el index fresco.
  const u = new URL(window.location.href);
  u.searchParams.set('_r', Date.now().toString(36));
  window.location.replace(u.toString());
}

// ── Root Error Boundary ───────────────────────────────────────────
class RootErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e, info: null }; }
  componentDidCatch(e, info) {
    console.error('[ARYES ROOT ERROR]', e, info);
    // Reportar a Sentry: un crash del boundary es un usuario real con la app rota
    // en su navegador (ej. chunk viejo tras un deploy). Sin esto, la Torre de
    // Control es ciega a los errores de cliente — sólo los veíamos de casualidad.
    try { Sentry.captureException(e, { tags: { boundary: 'root' }, extra: { componentStack: info?.componentStack } }); } catch {}
    this.setState({ info });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ position:'fixed', inset:0, background:'#fff', display:'flex',
          flexDirection:'column', alignItems:'center', justifyContent:'center',
          padding:32, fontFamily:'monospace', zIndex:99999 }}>
          <h2 style={{ color:'#dc2626', marginBottom:16, fontSize:18 }}>⛔ Error de aplicación</h2>
          <pre style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8,
            padding:16, fontSize:12, maxWidth:'90vw', overflowX:'auto',
            whiteSpace:'pre-wrap', wordBreak:'break-all', color:'#7f1d1d' }}>
            {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
          </pre>
          <button
            onClick={() => hardReset()}
            style={{ marginTop:20, background:'#dc2626', color:'#fff', border:'none',
              padding:'10px 20px', borderRadius:6, cursor:'pointer', fontSize:14 }}>
            Limpiar cache y recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Login Screen ──────────────────────────────────────────────────
function LoginScreen({ onLogin, onExplore }) {
  const [email, setEmail] = useState('');
  const [pass, setPass]   = useState('');
  const [err, setErr]     = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!email || !pass) { setErr('Ingresa tu email y contraseña'); return; }
    setLoading(true); setErr('');
    try {
      const r = await fetch(SB_URL + '/auth/v1/token?grant_type=password', {
        method: 'POST',
        headers: { 'apikey': SB_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: pass })
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.error_description || data.message || 'Credenciales incorrectas'); setLoading(false); return; }
      const userR = await fetch(SB_URL + '/rest/v1/users?email=eq.' + encodeURIComponent(email) + '&limit=1', {
        headers: { 'apikey': SB_KEY, 'Authorization': 'Bearer ' + data.access_token }
      });
      const users = await userR.json();
      // Fallback al user_metadata del JWT: en el alta self-service, register.js graba
      // org_id + role:'admin' en el metadata del auth. Si la fila public.users no está
      // (insert "non-fatal" que pudo fallar), no degradamos a operador/__no_org__ —
      // leemos el metadata para entrar como admin en la org correcta y ver el onboarding.
      const meta = data.user?.user_metadata || {};
      const role = users?.[0]?.role || meta.role || 'operador';
      const name = users?.[0]?.name || meta.nombre || email.split('@')[0];
      const username = users?.[0]?.username || email.split('@')[0];
      const orgId = users?.[0]?.org_id || meta.org_id || getOrgId();
      const expiresIn = (data.expires_in || 3600) * 1000;
      const session = { ...data, email, role, name, username, orgId, expiresAt: Date.now() + expiresIn };
      localStorage.setItem('aryes-session', JSON.stringify(session));
      onLogin(session);
    } catch {
      setErr('Error de conexión. Verifica tu internet.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9f9f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{'.au{animation:fadeUp .25s ease both;}@keyframes fadeUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}'}</style>
      <div className="au" style={{ background: '#fff', border: '1px solid #e2e2de', borderRadius: 12, padding: '40px 44px', width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/pazque-logo.png" alt="Pazque" style={{ height: 28, objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6a6a68', marginTop: 8 }}>Sistema de gestión comercial</p>
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ marginBottom: 5 }}><span style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6a6a68' }}>Email</span></div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com"
              onKeyDown={e => e.key === 'Enter' && handle()}
              style={{ width: '100%', fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#1a1a18', background: '#fff', border: '1px solid #e2e2de', padding: '9px 11px', borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ marginBottom: 5 }}><span style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6a6a68' }}>Contraseña</span></div>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="••••••••"
              onKeyDown={e => e.key === 'Enter' && handle()}
              style={{ width: '100%', fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#1a1a18', background: '#fff', border: '1px solid #e2e2de', padding: '9px 11px', borderRadius: 4 }} />
          </div>
          {err && <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#dc2626', textAlign: 'center' }}>{err}</p>}
          <button onClick={handle} disabled={loading}
            style={{ background: '#059669', color: '#fff', border: '1px solid #059669', fontFamily: "'Inter',sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '10px 22px', cursor: loading ? 'default' : 'pointer', width: '100%', opacity: loading ? 0.4 : 1, borderRadius: 4 }}>
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6a6a68', textAlign: 'center', marginTop: 8 }}>
            <a href="/reset-password" style={{ color: '#059669', fontWeight: 600, textDecoration: 'none' }}>¿Olvidaste tu contraseña?</a>
          </p>
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6a6a68', textAlign: 'center', marginTop: 8 }}>
            ¿No tienes cuenta?{' '}
            <a href="/register" style={{ color: '#059669', fontWeight: 600, textDecoration: 'none' }}>Registrarse gratis</a>
          </p>
          {onExplore && (
            <button onClick={onExplore}
              style={{ background: 'none', border: '1px solid #e2e2de', borderRadius: 8, padding: '10px 0', width: '100%', fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#1a1a18', cursor: 'pointer', marginTop: 12, transition: 'background .12s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9f9f7'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Explorar la plataforma →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ErrorBoundary: catches render crashes ─────────────────────────
class DemoErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, info: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('[DemoErrorBoundary] CAUGHT:', error?.message, error?.stack);
    console.error('[DemoErrorBoundary] Component stack:', info?.componentStack);
    this.setState({ info });
  }
  render() {
    if (this.state.error) {
      return React.createElement('div', { style: { padding: 40, fontFamily: 'monospace' } },
        React.createElement('h2', { style: { color: 'red' } }, 'Demo render error'),
        React.createElement('pre', { style: { whiteSpace: 'pre-wrap', fontSize: 12, background: '#f5f5f5', padding: 16, borderRadius: 8, maxHeight: 400, overflow: 'auto' } },
          this.state.error?.message + '\n\n' + (this.state.error?.stack || '') + '\n\nComponent:\n' + (this.state.info?.componentStack || '')
        ),
        React.createElement('button', { onClick: () => { this.setState({ error: null, info: null }); window.location.href = '/'; }, style: { marginTop: 16, padding: '8px 20px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' } }, 'Volver al inicio')
      );
    }
    return this.props.children;
  }
}

// ── Root: owns auth state, renders Login OR App ───────────────────
function Root() {
  useErrorReporting(); // captura errores JS globales y promises sin catch
  const [session, setSession] = useState(() => readSession());
  const { demoMode, demoIndustry, demoState, activateDemo, exitDemo, demoGuard } = useDemo();
  const [showDemoSelector, setShowDemoSelector] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('demo') === 'true') {
        window.history.replaceState({}, '', window.location.pathname);
        return true;
      }
    } catch {}
    return false;
  });

  // ── effectiveSession: demo fake o real ──
  
  // Auto-show demo selector if ?demo=true
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'true' && !demoMode) {
      setShowDemoSelector(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ── Feedback visual al volver de MercadoPago ──
  const [showUpgradeSuccess, setShowUpgradeSuccess] = React.useState(false);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === '1') {
      setShowUpgradeSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setShowUpgradeSuccess(false), 8000);
    }
  }, []);

  const effectiveSession = demoMode ? {
    email: 'demo@pazque.com', role: 'admin', name: 'Demo', orgId: 'demo',
    access_token: 'demo-token', expiresAt: Date.now() + 86400000, _demo: true,
  } : session;
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return !localStorage.getItem(ONBOARDING_KEY); }
    catch { return false; }
  });

  const handleLogin = (s) => { setSession(s); setSentryUser(s); };
  const handleLogout = () => {
    try {
      const s = readSession();
      if (s?.access_token) {
        fetch(SB_URL + '/auth/v1/logout', {
          method: 'POST',
          headers: { 'apikey': SB_KEY, 'Authorization': 'Bearer ' + s.access_token }
        }).catch(() => {});
      }
    } catch { /* non-blocking */ }
    localStorage.removeItem('aryes-session');
    setSentryUser(null);
    setSession(null);
  };

  // Verificar si el trial venció — mostrar pantalla de upgrade
  const [orgStatus, setOrgStatus] = React.useState(() => demoMode ? 'ok' : null); // null=loading, 'ok', 'expired', 'canceled'
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [upgradeDaysLeft, setUpgradeDaysLeft] = React.useState(null);
  React.useEffect(() => {
    const handler = () => setShowUpgrade(true);
    window.addEventListener('pazque-upgrade', handler);
    return () => window.removeEventListener('pazque-upgrade', handler);
  }, []);

  // Sync orgStatus immediately when demoMode activates (avoids white flash)
  React.useEffect(() => {
    if (demoMode && orgStatus !== 'ok') setOrgStatus('ok');
  }, [demoMode]);

  React.useEffect(() => {
    if (demoMode) { setOrgStatus('ok'); return; }
    if (!effectiveSession?.orgId) return;
    // Verificar estado de la org en Supabase
    const SB_URL = import.meta.env.VITE_SUPABASE_URL;
    const SB_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
    fetch(SB_URL + '/rest/v1/organizations?id=eq.' + encodeURIComponent(effectiveSession.orgId) + '&select=subscription_status,trial_ends_at,active&limit=1', {
      headers: { apikey: SB_KEY, Authorization: 'Bearer ' + effectiveSession.access_token }
    })
    .then(r => r.json())
    .then(orgs => {
      const org = orgs?.[0];
      if (!org) { setOrgStatus('ok'); return; } // si no hay org, dejar pasar (Pazque legacy)
      if (!org.active) { setOrgStatus('canceled'); return; }
      if (org.subscription_status === 'active') { setOrgStatus('ok'); return; }
      if (org.subscription_status === 'trial') {
        const trialEnd = org.trial_ends_at ? new Date(org.trial_ends_at) : null;
        if (!trialEnd || trialEnd > new Date()) { setOrgStatus('ok'); return; }
        setOrgStatus('expired');
        return;
      }
      if (org.subscription_status === 'past_due') { setOrgStatus('ok'); return; } // gracia de 3 días
      if (org.subscription_status === 'canceled') { setOrgStatus('canceled'); return; }
      if (org.subscription_status === 'pending_upgrade') {
        // 7 días de gracia para activar el plan full
        const upgraded = org.upgrade_requested_at ? new Date(org.upgrade_requested_at) : null;
        if (!upgraded || (Date.now() - upgraded.getTime()) > 7 * 24 * 60 * 60 * 1000) {
          setOrgStatus('pending_upgrade'); return;
        }
        // Dentro de gracia — mostrar días restantes
        const daysLeft = Math.max(0, Math.ceil((7 * 24 * 60 * 60 * 1000 - (Date.now() - upgraded.getTime())) / 86400000));
        setUpgradeDaysLeft(daysLeft);
        setOrgStatus('ok'); return;
      }
      setOrgStatus('ok'); // default: dejar pasar
    })
    .catch(() => setOrgStatus('ok')); // si falla el check, dejar pasar (no bloquear por error de red)
  }, [effectiveSession?.orgId, demoMode]);


  // Mostrar loading mientras verifica (máx 2 segundos en una buena conexión)
  // Demo selector
  if (showDemoSelector && !demoMode) {
    return <DemoSelector onSelect={(id) => {
      activateDemo(id);
      setShowDemoSelector(false);
    }} />;
  }
  if (!effectiveSession) return <LoginScreen onLogin={handleLogin} onExplore={() => setShowDemoSelector(true)} />;

  if (effectiveSession && orgStatus === null && !demoMode) return (
    <div style={{ minHeight: '100vh', background: '#f9f9f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #059669', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
  // Banner de upgrade pendiente (gracia)
  const UpgradeGraceBanner = upgradeDaysLeft !== null ? React.createElement('div', {
    style: { position:'fixed', top:0, left:0, right:0, zIndex:9998, background:'#fef2f2', color:'#dc2626', padding:'10px 24px', textAlign:'center', fontFamily:"'Inter',system-ui,sans-serif", fontSize:13, fontWeight:600, borderBottom:'1px solid #fecaca', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }
  }, 
    '⚠️ Tu plan de lanzamiento terminó. ',
    upgradeDaysLeft === 0 ? 'Hoy es el último día para activar tu plan.' : 'Te quedan ' + upgradeDaysLeft + ' día' + (upgradeDaysLeft !== 1 ? 's' : '') + ' para activar tu plan.',
    ' ',
    React.createElement('a', { href:'#', onClick: function(e) { e.preventDefault(); window.dispatchEvent(new CustomEvent('pazque-upgrade')); }, style: { color:'#dc2626', fontWeight:700, textDecoration:'underline', cursor:'pointer' } }, 'Activar ahora')
  ) : null;

  // Banner de pago exitoso
  const UpgradeSuccessBanner = showUpgradeSuccess ? React.createElement('div', {
    style: { position:'fixed', top:0, left:0, right:0, zIndex:9999, background:'#059669', color:'#fff', padding:'12px 24px', textAlign:'center', fontFamily:"'Inter',system-ui,sans-serif", fontSize:14, fontWeight:600, animation:'slideDown 0.3s ease' }
  }, '✅ ¡Pago recibido! Tu suscripción está activa. Bienvenido a Pazque.') : null;

  // Upgrade manual (desde el banner)
  if (showUpgrade && effectiveSession) return (
    <Suspense fallback={null}>
      <UpgradePage session={effectiveSession} reason="upgrade" />
    </Suspense>
  );

  // Trial vencido o cuenta cancelada → página de upgrade
  if (orgStatus === 'expired') return (
    <Suspense fallback={null}>
      <UpgradePage session={session} reason="trial_expired" />
    </Suspense>
  );
  if (orgStatus === 'canceled') return (
    <Suspense fallback={null}>
      <UpgradePage session={session} reason="canceled" />
    </Suspense>
  );
  if (orgStatus === 'pending_upgrade') return (
    <Suspense fallback={null}>
      <UpgradePage session={session} reason="pending_upgrade" />
    </Suspense>
  );
  return (
    <>
      {UpgradeGraceBanner}
      {UpgradeSuccessBanner}
      <AppProvider session={effectiveSession} onLogout={demoMode ? exitDemo : handleLogout} onSessionUpdate={setSession} demoState={demoMode ? demoState : null}>
        <Routes>
          <Route path="" element={<Navigate to="/app/dashboard" replace />} />
          <Route path=":tab" element={<PazqueApp session={effectiveSession} onLogout={demoMode ? () => { exitDemo(); setSession(null); } : handleLogout} onSessionUpdate={setSession} demoMode={demoMode} demoGuard={demoGuard} />} />
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
        {demoMode && <DemoToast />}
      </AppProvider>
      {/* El asistente de configuración inicial (logo, datos de la empresa) es cosa
          del ADMIN que monta la distribuidora. Un vendedor/operador/contador entra a
          una org YA configurada por su admin → nunca debe ver el wizard. Antes saltaba
          por rol cualquiera en un dispositivo nuevo (no había flag local) y parecía
          que "le pedía configurar todo". */}
      {showOnboarding && !demoMode && effectiveSession.role === 'admin' && (
        <Suspense fallback={null}>
          <OnboardingWizard
            session={session}
            onComplete={() => setShowOnboarding(false)}
            onSkip={() => setShowOnboarding(false)}
          />
        </Suspense>
      )}
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <RootErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          {/* Public catalog — no auth required */}
          <Route path="/catalogo" element={<CatalogoPage />} />
          {/* B2B order portal — OTP auth, no WMS session required */}
          <Route path="/pedidos" element={<PedidosPage />} />
          {/* Portal del vendedor — login con usuario interno, pasa pedidos por sus clientes */}
          <Route path="/vendedor" element={<VendedorPage />} />
          {/* Driver mobile view — no auth required, reads from Supabase directly */}
          <Route path="/driver" element={<DriverView />} />
          {/* Public client delivery tracking — no auth */}
          <Route path="/tracking" element={<TrackingPage />} />
          {/* Public self-registration */}
          <Route path="/" element={(() => {
            if (window.location.hash && window.location.hash.includes('access_token') && window.location.hash.includes('type=recovery')) {
              window.location.href = '/reset-password' + window.location.hash;
              return <div/>;
            }
            // Dominio de cliente (ej. pedidos.aryes.com.uy): la raíz "/" es SU portal,
            // no la web de Pazque. Genérico: cualquier host que no sea de Pazque
            // (pazque.com, *.vercel.app, localhost, aryes-stock) se trata como
            // dominio de distribuidora → portal de pedidos en la raíz.
            const h = window.location.hostname;
            const esHostPazque = h === 'localhost' || h === '127.0.0.1'
              || h.includes('vercel.app') || h.includes('aryes-stock')
              || h === 'pazque.com' || h === 'www.pazque.com';
            if (!esHostPazque) return <PedidosPage />;
            return <Suspense fallback={<div/>}><LandingPage /></Suspense>;
          })()} />
          <Route path="/landing" element={<Suspense fallback={<div/>}><LandingPage /></Suspense>} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/terms" element={<Suspense fallback={<div/>}><TermsPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<div/>}><PrivacyPage /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<div/>}><ResetPasswordPage /></Suspense>} />
          {/* Consola privada del dueño (Federico). Auth propia por OWNER_KEY,
              separada del sistema de roles por-org. Fuera de buscadores. */}
          <Route path="/owner" element={<Suspense fallback={<div/>}><OwnerPage /></Suspense>} />
          {/* Upgrade / pricing page */}
          <Route path="/upgrade" element={<UpgradePage session={null} reason="upgrade" />} />
          {/* Everything else → authenticated app */}
          <Route path="/app/*" element={<Root />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </RootErrorBoundary>
);

// ── PWA Service Worker registration + auto-update ────────────
// El SW cachea la app. Sin esto, al publicar una versión nueva el cliente seguía
// viendo la vieja hasta cerrar y reabrir la app a mano (problema recurrente en el
// PWA instalado). Ahora, cuando el SW nuevo toma control (skipWaiting+claim),
// recargamos automáticamente para servir el código fresco. El carrito y la sesión
// viven en localStorage, así que la recarga no pierde nada.
if ('serviceWorker' in navigator) {
  // Si ya había un SW controlando la página, un cambio de controller = versión
  // nueva activada → recargamos una sola vez. En la PRIMERA visita no hay
  // controller previo, así que no recarga (la página ya trae el código fresco).
  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !hadController) return;
    reloading = true;
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      // Chequear updates al volver a la app (foco) — el navegador no siempre
      // revalida el sw.js solo en PWAs standalone.
      const checkUpdate = () => { reg.update().catch(() => {}); };
      window.addEventListener('focus', checkUpdate);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkUpdate();
      });
    }).catch(() => {});
  });
}
